import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { MessageSquare, ArrowUpRight, Bot } from 'lucide-react';
import { api, unwrap } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';

interface Conversation {
  id: string;
  agent_class: string;
  session_id: string | null;
  visitor_ip: string | null;
  message_count: number;
  messages_count?: number;
  leads_count?: number;
  last_message_at: string | null;
  captured_lead: Record<string, unknown> | null;
}

interface Paginated<T> {
  data: T[];
  total: number;
  current_page: number;
  last_page: number;
}

export const Route = createFileRoute('/_app/ai/conversations/')({
  component: ConversationsListPage,
});

function ConversationsListPage() {
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['ai-conversations', page],
    queryFn: () =>
      unwrap<Paginated<Conversation>>(api.get(`/ai/conversations?page=${page}&per_page=25`)),
  });

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6 md:p-8">
      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold tracking-tight">
            <MessageSquare className="size-7 text-neutral-700" /> Conversaciones del chatbot
          </h1>
          <p className="text-sm text-neutral-500">
            Histórico de interacciones con visitantes.
          </p>
        </div>
        <Button asChild variant="outline">
          <Link to="/ai/leads">
            <Bot className="size-4" /> Ver leads
          </Link>
        </Button>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Sesiones</CardTitle>
          <CardDescription>{data?.total ?? 0} conversaciones registradas.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-y border-neutral-200 bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-2">ID</th>
                <th className="px-4 py-2">Agente</th>
                <th className="px-4 py-2">Mensajes</th>
                <th className="px-4 py-2">Leads</th>
                <th className="px-4 py-2">Último mensaje</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-neutral-400">Cargando…</td></tr>
              )}
              {data?.data.length === 0 && !isLoading && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-neutral-400">Sin conversaciones.</td></tr>
              )}
              {data?.data.map((c) => (
                <tr key={c.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                  <td className="px-4 py-3 font-mono text-xs text-neutral-500">{c.id.slice(0, 8)}…</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline">{c.agent_class.split('\\').pop()}</Badge>
                  </td>
                  <td className="px-4 py-3 tabular-nums">{c.messages_count ?? c.message_count}</td>
                  <td className="px-4 py-3">
                    {(c.leads_count ?? 0) > 0
                      ? <Badge variant="success">{c.leads_count}</Badge>
                      : <span className="text-xs text-neutral-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-500">
                    {c.last_message_at ? new Date(c.last_message_at).toLocaleString('es') : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to="/ai/conversations/$id"
                      params={{ id: c.id }}
                      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                    >
                      Abrir <ArrowUpRight className="size-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data && data.last_page > 1 && (
            <div className="flex items-center justify-between border-t border-neutral-200 px-4 py-3 text-xs text-neutral-500">
              <span>Página {data.current_page} de {data.last_page}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
                <Button size="sm" variant="outline" disabled={page >= data.last_page} onClick={() => setPage(page + 1)}>Siguiente</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
